import { Box, Typography, Button, Stack } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { FilePicker } from '@capawesome/capacitor-file-picker'
import BasicModal from '../../../../components/modal/BasicModal'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { selectRestaurant } from '../../../../store/restaurant/setting'
import { uploadMenuScanThunk, saveMenuFromScanThunk } from '../../../../store/menuScanSlice'
import { ROUTES } from '../../../../helpers/routes-helper'
import { errorLog } from '../../../../utils/log-helper'

interface IMenuCardProps {
  openEditModel: boolean
  setOpenEditModel: React.Dispatch<React.SetStateAction<boolean>>
}

const LoadMenuDialog: React.FC<IMenuCardProps> = ({
  openEditModel,
  setOpenEditModel,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const restaurant = useAppSelector(selectRestaurant)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const isNative = Capacitor.isNativePlatform()

  const handleCancel = (): void => {
    if (isLoading) return
    setSelectedFile(null)
    setOpenEditModel(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
  }

  const handleChoosePhoto = async (): Promise<void> => {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        allowEditing: false,
      })
      if (!photo.dataUrl) return
      const res = await fetch(photo.dataUrl)
      const blob = await res.blob()
      setSelectedFile(
        new File([blob], `menu-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' }),
      )
    } catch (e) {
      errorLog('[LoadMenuDialog] Camera error', e)
    }
  }

  const handleChoosePdf = async (): Promise<void> => {
    try {
      const result = await FilePicker.pickFiles({
        types: ['application/pdf'],
        limit: 1,
        readData: true,
      })
      const picked = result.files[0]
      if (!picked?.data) return
      const byteChars = atob(picked.data)
      const bytes = new Uint8Array(byteChars.length)
      for (let i = 0; i < byteChars.length; i++) {
        bytes[i] = byteChars.charCodeAt(i)
      }
      setSelectedFile(new File([bytes], picked.name, { type: 'application/pdf' }))
    } catch (e) {
      errorLog('[LoadMenuDialog] PDF picker error', e)
    }
  }

  const handleConfirm = async (): Promise<void> => {
    if (!selectedFile || !restaurant?.restaurantId) return
    setIsLoading(true)
    try {
      const uploadResult = await dispatch(
        uploadMenuScanThunk({ file: selectedFile, restaurantId: restaurant.restaurantId }),
      ).unwrap()

      await dispatch(
        saveMenuFromScanThunk({
          scanId: uploadResult.scanId,
          request: {
            restaurantId: restaurant.restaurantId,
            dishes: uploadResult.dishes ?? [],
          },
        }),
      ).unwrap()

      setOpenEditModel(false)
      navigate(ROUTES.RESTAURANT_MENUS)
    } catch (e) {
      errorLog('[LoadMenuDialog] Scan failed', e)
    } finally {
      setIsLoading(false)
      setSelectedFile(null)
    }
  }

  return (
    <BasicModal
      open={openEditModel}
      onCancel={handleCancel}
      onConfirm={() => {
        void handleConfirm()
      }}
      isLoader={isLoading}
      title="Scan Menu"
      buttonName="Scan & save"
      saveBtnDisabled={!selectedFile || isLoading}
      handleClose={handleCancel}
    >
      <Box mt={2} mb={2}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload a photo or PDF of your menu and we&apos;ll extract and save the dishes automatically.
        </Typography>

        {isNative ? (
          <Stack spacing={1}>
            <Button
              variant={selectedFile?.type.startsWith('image') ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => { void handleChoosePhoto() }}
              disabled={isLoading}
            >
              {selectedFile?.type.startsWith('image') ? selectedFile.name : 'Take or choose a photo'}
            </Button>
            <Button
              variant={selectedFile?.type === 'application/pdf' ? 'contained' : 'outlined'}
              fullWidth
              onClick={() => { void handleChoosePdf() }}
              disabled={isLoading}
            >
              {selectedFile?.type === 'application/pdf' ? selectedFile.name : 'Choose a PDF'}
            </Button>
          </Stack>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              fullWidth
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
            >
              {selectedFile ? selectedFile.name : 'Choose file (image or PDF)'}
            </Button>
          </>
        )}
      </Box>
    </BasicModal>
  )
}

export default LoadMenuDialog
